import { MediaMatcher } from "@angular/cdk/layout";
import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSidenav } from "@angular/material/sidenav";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { AuthUser } from "app/core/auth/core/auth-user.model";
import {
    ResetPassword,
    SignIn,
    SignOut,
} from "app/core/auth/core/auth.actions";
import { AuthState } from "app/core/auth/core/auth.state";
import { NEVER, Observable, Subject, interval } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { AnnualDeclarationState } from "~app/company/form/annual-declaration-form/shared/state/annual-declaration.state";
import { SubscriptionState } from "~app/farp-odq/store/subscription";
import { InscriptionState } from "~app/inscription/store/cotisation-form";
import { VersioningService } from "~app/services/versioning.service";
import { SessionTimeoutDialogComponent } from "~app/shared/session-timeout-dialog/session-timeout-dialog.component";
import { LogService } from "../../../core/logger";
import { LocalSettingsService } from "../../../core/storage";

interface State {
  count: boolean;
  countup: boolean;
  speed: number;
  value: number;
  increase: number;
}

/** @title Responsive sidenav */
@Component({
    selector: "odq-full-layout",
    templateUrl: "full.component.html",
    styleUrls: ["full.component.scss"],
    standalone: false
})
export class FullComponent implements OnInit, OnDestroy {
  @Select(AuthState.signedIn) signedIn$: Observable<boolean>;
  @Select(AuthState.user) user$: Observable<AuthUser>;
  @Select(AuthState.loading) authLoading$: Observable<boolean>;
  @Select(AuthState.tokenExpiring) tokenExpiring$: Observable<number>;

  mobileQuery: MediaQueryList;
  minisidebar: boolean;
  sidebarOpened;
  timeLeft: number;

  dark = false;
  blue = false;

  isAuthorized: boolean;
  user: AuthUser;

  isIE = false;

  private mobileQueryListener: () => void;
  sidebarInfoVisible = false;

  @ViewChild("snav") sidenav: MatSidenav;
  private destroy$ = new Subject<void>();

  @Select(InscriptionState.processingInscription)
  processingInscription$: Observable<boolean>;

  @Select(SubscriptionState.processingFARPSubscription)
  processingFARPSubscription$: Observable<boolean>;

  @Select(AnnualDeclarationState.processingCommpanySubscription)
  processingCompanySubscription$: Observable<boolean>;

  currentVersion: string;
  latestVersion: string;
  isOldBrowser: boolean;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private translate: TranslateService,
    private localSettings: LocalSettingsService,
    private logger: LogService,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private versioningService: VersioningService,
    private localSettingsService: LocalSettingsService
  ) {
    this.detectOldBrowsers();

    this.mobileQuery = media.matchMedia("(min-width: 1099px)");
    this.mobileQueryListener = () => changeDetectorRef.detectChanges();
    // tslint:disable-next-line: deprecation
    this.mobileQuery.addListener(this.mobileQueryListener);

    this.setCurrentVersion();
    this.setLatestVersion();

    setInterval(() => this.setLatestVersion(), 60000);
  }

  ngOnInit() {
    this.signedIn$.pipe(takeUntil(this.destroy$)).subscribe((isAuthorized) => {
      this.isAuthorized = isAuthorized;
    });

    this.user$.pipe(takeUntil(this.destroy$)).subscribe((authUser) => {
      this.user = authUser;
      this.logger.debug("current User", this.user);
    });

    const tokenExpiringCountDown$ = this.tokenExpiring$.pipe(
      switchMap((expireAt) =>
        expireAt > 0
          ? interval(1000).pipe(
              map(() => {
                return Math.floor(expireAt - Date.now() / 1000);
              })
            )
          : NEVER
      ),
      takeUntil(this.destroy$)
    );

    tokenExpiringCountDown$.subscribe((timeleft) => {
      this.timeLeft = timeleft;
      if (timeleft === 235) this.showSessionTimeoutDialog();
    });

  }

  ngOnDestroy(): void {
    // tslint:disable-next-line: deprecation
    this.mobileQuery.removeListener(this.mobileQueryListener);

    this.destroy$.next();
    this.destroy$.complete();
  }

  setCurrentVersion() {
    this.currentVersion = this.versioningService.getCurrentVersion();
  }

  setLatestVersion() {
    this.versioningService.getLatestVersion().subscribe((data) => {
      this.latestVersion = data.version;
    });
  }

  // Detect browsers that dont support media-queries
  detectOldBrowsers() {
    const isSafari = /^((?!chrome|android).)*safari/i.test(
      window.navigator.userAgent
    );

    if (isSafari) this.isOldBrowser = this.checkOldSafari();
  }

  login() {
    this.store.dispatch(new SignIn(this.router.url));
  }

  logout() {
    console.log("Full component logout");
    this.localSettingsService.clearBrowserData();
    this.store.dispatch(new SignOut());
    this.router.navigate(["/home"]);
  }

  resetPassword() {
    this.store.dispatch(new ResetPassword());
  }

  useLanguage(lang: string) {
    this.localSettings.setLanguage(lang);
    this.translate.use(lang);
  }

  isSidebarInfoVisible(): boolean {
    return this.sidebarInfoVisible;
  }

  onItemClick() {
    if (!this.mobileQuery.matches) {
      this.sidenav.close();
    }
  }

  checkOldSafari(): boolean {
    const pattern = `.+Version\/(.+) Safari`;
    const regex = new RegExp(pattern, "i");
    const match = regex.exec(window.navigator.appVersion);

    if (match === null || match.length === 0) return false;
    const currentVersion = parseFloat(match[1]);

    return !Number.isNaN(currentVersion) && currentVersion < 16.5; //Using Browserstack, Safari 16.5 does not have the menu overlap problem
  }

  showSessionTimeoutDialog() {
    const dialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      maxWidth: "400px",
      disableClose: true,
    });

    this.router.events.subscribe(() => {
      dialogRef.close();
    });
  }
}
